import { createElement, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate, useParams } from 'react-router-dom';
import { type ContextualNavSection } from '@vnetwork/ui/layout';
import { useSetContextualNav } from '@/components/layout/nav-context';
import { useProject } from '@/api/projects';
import { useRegisterProject } from '@/api/healthcheck';
import { HEALTHCHECK_NAV_GROUPS, hcHref, hcActiveId } from './nav';
import { MonitorsDashboard } from './MonitorsDashboard';
import { WatchpointList } from './monitors/WatchpointList';
import { WatchpointDetail } from './monitors/WatchpointDetail';
import { WatchpointCreate } from './monitors/WatchpointCreate';
import { IncidentList } from './incidents/IncidentList';
import { KumaUptimePage } from './kuma-uptime/KumaUptimePage';
import { KumaMonitorCreate } from './kuma-uptime/KumaMonitorCreate';
import { KumaMonitorDetail } from './kuma-uptime/KumaMonitorDetail';
import { ChannelList } from './notifications/ChannelList';
import { ChannelCreate } from './notifications/ChannelCreate';
import { ChannelDetail } from './notifications/ChannelDetail';
import { AlarmList } from './alarms/AlarmList';
import { AlarmCreate } from './alarms/AlarmCreate';
import { PlaybookList } from './playbooks/PlaybookList';
import { PlaybookCreate } from './playbooks/PlaybookCreate';
import { PlaybookDetail } from './playbooks/PlaybookDetail';

/**
 * Project-scoped monitoring console. The per-project navigation lives in the
 * app's main `ContextualSidebar` (registered via `useSetContextualNav`) — entering
 * the console swaps the whole sidebar to its scope with a "Back to Services" link.
 * Each sub-page supplies its own page chrome (Container + PageHeader).
 */
export function HealthcheckConsole() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { projectId } = useParams<{ projectId: string }>();
  const base = `/services/healthcheck/${projectId}`;

  // Ensure the project is registered in the monitoring service before any
  // watchpoint create (FK requires the project row). Idempotent, fire-and-forget.
  const { data: project } = useProject(projectId);
  const register = useRegisterProject();
  useEffect(() => {
    if (projectId) register.mutate({ id: projectId, name: project?.name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, project?.name]);

  useSetContextualNav(() => {
    const sections: ContextualNavSection[] = HEALTHCHECK_NAV_GROUPS.map((group) => ({
      heading: group.heading,
      items: group.items.map((entry) => {
        const href = hcHref(base, entry.slug);
        return {
          id: href,
          label: entry.label,
          icon: createElement(entry.icon, { size: 16 }),
          onSelect: () => navigate(href),
        };
      }),
    }));
    return {
      sections,
      activeId: hcActiveId(base, pathname),
      back: { label: 'Back to Services', onSelect: () => navigate('/services') },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, pathname, navigate]);

  return (
    <Routes>
      <Route index element={<MonitorsDashboard />} />
      <Route path="monitors" element={<WatchpointList />} />
      <Route path="monitors/create" element={<WatchpointCreate />} />
      <Route path="monitors/:watchpointId" element={<WatchpointDetail />} />
      <Route path="kuma-uptime" element={<KumaUptimePage />} />
      <Route path="kuma-uptime/create" element={<KumaMonitorCreate />} />
      <Route path="kuma-uptime/:monitorName" element={<KumaMonitorDetail />} />
      <Route path="incidents" element={<IncidentList />} />
      {/* Phase 1 — Notifications (channels + subscribers) */}
      <Route path="notifications" element={<ChannelList />} />
      <Route path="notifications/create" element={<ChannelCreate />} />
      <Route path="notifications/:channelId" element={<ChannelDetail />} />
      {/* Phase 2 — Alarms */}
      <Route path="alarms" element={<AlarmList />} />
      <Route path="alarms/create" element={<AlarmCreate />} />
      <Route path="alarms/:alarmId" element={<AlarmList />} />
      {/* Phase 3 — Playbooks */}
      <Route path="playbooks" element={<PlaybookList />} />
      <Route path="playbooks/create" element={<PlaybookCreate />} />
      <Route path="playbooks/:playbookId" element={<PlaybookDetail />} />
      <Route path="*" element={<MonitorsDashboard />} />
    </Routes>
  );
}
